<div class="dashboard-shell space-y-6 pb-10">
  @if (loading()) {
  <div class="dashboard-loading">
    <div class="loading-orbit">
      <span class="loading loading-spinner loading-lg text-primary"></span>
    </div>
    <div>
      <p class="font-black text-base-content">Preparing your dashboard</p>
      <p class="mt-1 text-xs text-base-content/45">
        Syncing orders, sales, customers and riders
      </p>
    </div>
  </div>
  } @else { @if (error()) {
  <div class="alert alert-error rounded-2xl border border-error/25 shadow-sm">
    <i class="fa-solid fa-triangle-exclamation"></i>
    <span class="font-semibold">{{ error() }}</span>
    <button class="btn btn-sm btn-ghost ml-auto" (click)="loadDashboard()">
      Retry
    </button>
  </div>
  }

  <section class="hero-panel">
    <div class="hero-grid"></div>
    <div class="hero-glow hero-glow-one"></div>
    <div class="hero-glow hero-glow-two"></div>

    <div
      class="relative z-10 flex flex-col gap-6 xl:flex-row xl:items-end xl:justify-between"
    >
      <div>
        <div class="live-pill">
          <span class="live-dot"></span>
          Live operations <span class="opacity-50">•</span>
          {{ currentTime() | date : 'h:mm:ss a' }}
        </div>
        <p
          class="mt-7 text-xs font-black uppercase tracking-[0.24em] text-white/45"
        >
          {{ selectedStoreName }}
        </p>
        <h1
          class="mt-2 max-w-3xl text-3xl font-black leading-tight tracking-tight text-white sm:text-5xl"
        >
          Everything happening at Supidoo,
          <span class="text-emerald-300">in one view.</span>
        </h1>
        <p
          class="mt-3 max-w-2xl text-sm font-medium leading-relaxed text-white/55"
        >
          Monitor demand, fulfilment and your delivery fleet with live
          operational data.
        </p>
      </div>

      <div class="flex flex-wrap items-center gap-2">
        @if (!isStoreManager()) {
        <label class="hero-select-wrap">
          <i class="fa-solid fa-shop text-white/45"></i>
          <select
            class="hero-select"
            [ngModel]="selectedStoreId()"
            (ngModelChange)="onStoreChange($event)"
            aria-label="Select store"
          >
            <option [ngValue]="null">All stores</option>
            @for (store of stores(); track store.id) {
            <option [ngValue]="store.id">{{ store.name }}</option>
            }
          </select>
        </label>
        }
        <button class="hero-button" routerLink="/orders">
          View orders <i class="fa-solid fa-arrow-right text-[10px]"></i>
        </button>
        <button
          class="hero-icon-button"
          (click)="loadDashboard()"
          aria-label="Refresh dashboard"
        >
          <i class="fa-solid fa-rotate"></i>
        </button>
      </div>
    </div>
  </section>

  <section class="grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-4">
    @for (metric of topMetrics(); track metric.label; let index = $index) {
    <article class="metric-card" [attr.data-accent]="index">
      <div class="metric-card-top">
        <div class="metric-icon" [ngClass]="metric.iconClass">
          <i [class]="metric.icon"></i>
        </div>
        <span class="metric-badge" [ngClass]="metric.badgeClass">
          <span class="h-1.5 w-1.5 rounded-full bg-current"></span
          >{{ metric.badge }}
        </span>
      </div>
      <p class="metric-label">{{ metric.label }}</p>
      <p class="metric-value">{{ metric.value }}</p>
      <p class="metric-hint">{{ metric.hint }}</p>
      <div class="metric-watermark"><i [class]="metric.icon"></i></div>
    </article>
    }
  </section>

  <section class="grid grid-cols-1 gap-6 xl:grid-cols-12">
    <article class="surface-card xl:col-span-8">
      <header class="surface-header">
        <div>
          <p class="eyebrow">Performance</p>
          <h2 class="section-title">7-day sales movement</h2>
          <p class="section-copy">
            Daily order value for {{ selectedStoreName }}
          </p>
        </div>
        <div class="text-right">
          <p class="text-xs font-bold text-base-content/40">Seven-day sales</p>
          <p class="mt-1 text-xl font-black text-base-content">
            {{ weeklySales() | indianCompact:true }}
          </p>
        </div>
      </header>

      <div class="chart-wrap"><canvas #dashboardChart></canvas></div>

      <footer class="chart-footer">
        <div>
          <p class="chart-stat-label">Average order today</p>
          <p class="chart-stat-value">
            ₹{{ averageOrderValue() | number : '1.0-0' }}
          </p>
        </div>
        <div class="h-9 w-px bg-base-300"></div>
        <div>
          <p class="chart-stat-label">Last refreshed</p>
          <p class="chart-stat-value">{{ lastUpdated() | date : 'h:mm a' }}</p>
        </div>
      </footer>
    </article>

    <article class="surface-card xl:col-span-4">
      <header class="surface-header">
        <div>
          <p class="eyebrow">Today</p>
          <h2 class="section-title">Order pipeline</h2>
          <p class="section-copy">Live fulfilment workload</p>
        </div>
        <button class="small-link" routerLink="/orders">
          Manage <i class="fa-solid fa-chevron-right text-[9px]"></i>
        </button>
      </header>

      <div class="space-y-3 p-5 pt-1">
        @for (status of todayStatus(); track status.label) {
        <div class="pipeline-row">
          <div class="pipeline-icon" [ngClass]="status.colorClass">
            <i [class]="status.icon"></i>
          </div>
          <div class="min-w-0 flex-1">
            <p class="truncate text-sm font-bold text-base-content/70">
              {{ status.label }}
            </p>
            <div class="mt-2 h-1.5 overflow-hidden rounded-full bg-base-200">
              <div
                class="h-full min-w-1 rounded-full transition-all"
                [ngClass]="status.progressClass"
                [style.width.%]="
                  topMetrics()[0] && +topMetrics()[0].value.replace(',', '') > 0
                    ? (status.value / +topMetrics()[0].value.replace(',', '')) *
                      100
                    : 0
                "
              ></div>
            </div>
          </div>
          <span class="pipeline-value">{{ status.value }}</span>
        </div>
        }
      </div>

      <div
        class="mx-5 mb-5 rounded-2xl border border-primary/15 bg-primary/5 p-4"
      >
        <div class="flex items-start gap-3">
          <div
            class="flex h-9 w-9 shrink-0 items-center justify-center rounded-xl bg-primary text-primary-content"
          >
            <i class="fa-solid fa-wand-magic-sparkles text-xs"></i>
          </div>
          <div>
            <p class="text-sm font-black text-base-content">
              Plan before demand rises
            </p>
            <p class="mt-1 text-xs leading-relaxed text-base-content/50">
              Use sales, searches and upcoming events to decide what to restock.
            </p>
            <button
              class="mt-2 text-xs font-black text-primary"
              routerLink="/reports/search"
            >
              Open demand planner
              <i class="fa-solid fa-arrow-right ml-1 text-[9px]"></i>
            </button>
          </div>
        </div>
      </div>
    </article>
  </section>

  <section class="grid grid-cols-1 gap-6 xl:grid-cols-12">
    <article class="surface-card overflow-hidden xl:col-span-8">
      <header class="surface-header border-b border-base-200">
        <div>
          <div class="flex items-center gap-2">
            <span class="live-dot"></span>
            <p class="eyebrow">Live feed</p>
          </div>
          <h2 class="section-title">Recent orders</h2>
          <p class="section-copy">Latest transactions and fulfilment status</p>
        </div>
        <button class="small-link" routerLink="/orders">
          View all <i class="fa-solid fa-arrow-right text-[9px]"></i>
        </button>
      </header>

      @if (recentOrders().length === 0) {
      <div class="empty-state">
        <div class="empty-icon"><i class="fa-solid fa-bag-shopping"></i></div>
        <p class="font-black text-base-content">No recent orders</p>
        <p class="text-xs text-base-content/45">
          New transactions will appear here automatically.
        </p>
      </div>
      } @else {
      <div class="overflow-x-auto">
        <table class="table dashboard-table w-full">
          <thead>
            <tr>
              <th>Order</th>
              <th>Customer</th>
              <th>Store</th>
              <th>Status</th>
              <th class="text-right">Amount</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            @for (order of recentOrders(); track order.id) {
            <tr
              [routerLink]="['/orders/detail', order.id]"
              class="cursor-pointer"
            >
              <td>
                <p class="font-black text-base-content">#{{ order.id }}</p>
                <p class="mt-0.5 text-[10px] text-base-content/40">
                  {{ order.created_at | date : 'dd MMM, h:mm a' }}
                </p>
              </td>
              <td>
                <div class="flex items-center gap-2.5">
                  <div class="avatar-placeholder">
                    {{ (order.user?.name || 'G').charAt(0) | uppercase }}
                  </div>
                  <span
                    class="max-w-32 truncate text-sm font-bold capitalize"
                    >{{ order.user?.name || 'Guest' }}</span
                  >
                </div>
              </td>
              <td>
                <span class="text-xs font-semibold text-base-content/55">{{
                  order.store?.store_name || selectedStoreName
                }}</span>
              </td>
              <td>
                <span
                  class="badge badge-sm badge-outline status-badge"
                  [ngClass]="getStatusClass(order.status)"
                >
                  {{ order.status.replace('_', ' ') }}
                </span>
              </td>
              <td class="text-right font-black text-base-content">
                ₹{{ order.payable_amount | number : '1.0-0' }}
              </td>
              <td class="pr-5 text-right text-base-content/25">
                <i class="fa-solid fa-chevron-right text-[10px]"></i>
              </td>
            </tr>
            }
          </tbody>
        </table>
      </div>
      }
    </article>

    <aside class="surface-card xl:col-span-4">
      <header class="surface-header">
        <div>
          <p class="eyebrow">Shortcuts</p>
          <h2 class="section-title">Quick actions</h2>
          <p class="section-copy">Jump into everyday operations</p>
        </div>
      </header>

      <div class="grid grid-cols-2 gap-3 p-5 pt-1">
        <button class="action-tile action-primary" routerLink="/orders">
          <span class="action-icon"
            ><i class="fa-solid fa-list-check"></i
          ></span>
          <span><strong>Orders</strong><small>Manage queue</small></span>
        </button>
        <button class="action-tile" routerLink="/inventory">
          <span class="action-icon"
            ><i class="fa-solid fa-boxes-stacked"></i
          ></span>
          <span><strong>Inventory</strong><small>Check stock</small></span>
        </button>
        <button class="action-tile" routerLink="/reports/search">
          <span class="action-icon"
            ><i class="fa-solid fa-chart-line"></i
          ></span>
          <span><strong>Demand</strong><small>Plan stock</small></span>
        </button>
        <button class="action-tile" routerLink="/marketing/promotions/create">
          <span class="action-icon"><i class="fa-solid fa-bullhorn"></i></span>
          <span><strong>Promotion</strong><small>Create offer</small></span>
        </button>
        @if (!isStoreManager()) {
        <button class="action-tile" routerLink="/products/add">
          <span class="action-icon"><i class="fa-solid fa-plus"></i></span>
          <span><strong>Product</strong><small>Add catalog item</small></span>
        </button>
        <button class="action-tile" routerLink="/stores/add">
          <span class="action-icon"><i class="fa-solid fa-store"></i></span>
          <span><strong>Store</strong><small>Add location</small></span>
        </button>
        }
      </div>
    </aside>
  </section>
  }
</div>
